import { Component, Input, ViewChild, AfterViewInit, EventEmitter, Output, Injector, ViewContainerRef, ChangeDetectorRef, OnDestroy, Type, OnInit } from '@angular/core';
import * as jp from 'jsonpath';
import { Observable, Subject, of, from, Subscription, forkJoin } from 'rxjs';
import { Column, ObjectProperties } from './models/column.interface';
import { AgGridAngular } from 'ag-grid-angular';

import { GridOptions, ColDef, RowSelectionOptions, ApplyColumnStateParams, GridApi, NumberCellEditor } from 'ag-grid-community';
import { DetailsComponent } from './columns/details/details.component';
import { ComplianceComponent } from './columns/compliance/compliance.component';
import { FlaggedComponent } from './columns/flagged/flagged.component';
import { ValueFormatterFactory, ValueFormatter, DomainOptions, FormatterOptions } from './formatters/data-table.formatters';
import { tap, switchMap } from 'rxjs/operators';
import { ToolBarService } from './tool-bar/tool-bar.service';
import { TableSettings, defaultTableSettings } from './models/table-settings.interface';
import { IconComponent } from './columns/icon/icon.component';
import { UrlComponent } from './columns/url/url.component';
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { GridFormatterService } from "../../services/grid-formatter.service";
import { SelectionIndicatorRendererComponent } from '../ag-grid/selection-indicator-renderer.component';
import { LunaDomain } from '../../../builder/models/domain.model';

@Component({
    selector: 'data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    //changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ToolBarService],
    standalone: false
})
export class DataTableComponent implements AfterViewInit, OnDestroy, OnInit {
    public _options?: GridOptions;
    public api: GridApi;
    public _columns: Column[] = [];
    public _config: any;
    public _settings: TableSettings;
    public _resolvedData: any[];
    public _selectedItems: any[] = [];
    public _onSelectedItemsChange: Subject<any[]> = new Subject();
    private subscriptions = new Subscription();
    private domains$: Observable<any> = of([]);
	@ViewChild('toolbarHost', { read: ViewContainerRef, static: true }) toolbarHost: ViewContainerRef;
    @ViewChild(AgGridAngular) grid: AgGridAngular
    data$: Observable<any>;
    gridCols: ColDef[] = [];
    domains: LunaDomain[] = [];
    protected set selectItems(val: any[]) {
        this._selectedItems = val;
        this._onSelectedItemsChange.next(this._selectedItems);
    }
    @Input() hidden: boolean = false;
    @Input() ariaLabel: string | undefined;
    get selectedItems() { return this._selectedItems; }

    get onSelectedItemsChange() { return this._onSelectedItemsChange.asObservable(); }

    private dataLoaded: boolean = false;

    @Output()
    onRendererInteraction: EventEmitter<{ rendererType: string, row: any, colHeader: string }> = new EventEmitter();

    @Output() onDataLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() gridIsReady: EventEmitter<boolean> = new EventEmitter<boolean>(false);

    @Input()
    set componentConfig(val: any) {
        this._config = val;
        this._addAdditionalColumns();
    };

    @Input()
    set gridOptions(val: GridOptions) {
        this._options = val;

        if (this._options) {
            //this._options.frameworkComponents = componentFrameworks;
            this._options.context = {
                ...this._options.context,
                componentParent: this
            }
            this._options.onGridReady = (params) => {
                this.api = params.api;
            };
        }
    }

    private _enabled: boolean = true;

    @Input()
    set enabled(val: boolean) {
        this._enabled = val;
        if (val)
            this.enableSelection();
        else
            this.disableSelection();
    }
    get enabled(): boolean {
        return this._enabled;
    }

    @Input()
    set settings(settings: TableSettings) {
        this._settings = Object.assign(defaultTableSettings(), settings);
        // this._toolbarService.addToolbars(this._settings.toolbars, this.toolbarHost);
    }

    get gridOptions(): GridOptions | undefined | null {
        return this._options;
    }

    buildColumns(cols: Column[]) {
        if (!cols)
            return;

        this.gridCols = [];
        this._columns = cols;

        cols.forEach(x => {
			if (x.printable == null)
                x.printable = true;

            if (x.objectProperty) {
                let path = x.objectProperty.path;

                //Find the relevant domain, if necessary
                if (x.valueFormatter == ValueFormatter.Domain) {
                    let typedOptions = x.formatterOptions as FormatterOptions<DomainOptions>;
                    let domainValues = this.domains.filter(x => x.id == typedOptions.params.domain);
                    if (domainValues && domainValues.length > 0) {
                        typedOptions.params.domainValues = domainValues[0];
                    }
                    x.formatterOptions = typedOptions;
                }

				var valueGetter = ValueFormatterFactory.createFormatter(this._injector, x.valueFormatter, x.formatterOptions);
				let col = {
                    headerClass: "wrapped",
                    colId: x.name,
                    // index: this.gridCols.length,
					headerName: x.name,
					headerTooltip: x.name,
					//If a valuegetter exists, don't set the tooltipField value, so that it can fall  through to the tooltipValueGetter object
					// tooltipField: valueGetter() ? null : x.useContentAsTooltip ? path.charAt(0) === '$' ? path.substring(2) : path : null,
					//If a separate tooltip getter has been explicitly specified use it, else use the valuegetter
					tooltipValueGetter: x.tooltipValueGetter ? x.tooltipValueGetter : valueGetter,
					field: path.charAt(0) === '$' ? path.substring(2) : path,
					//cellRenderer: x.renderer && x.renderer.toLocaleLowerCase() === 'default' ? '' : x.renderer,
					cellRendererParams: x.rendererParams,
					cellStyle: x.cellStyle,
					cellClass: x.cellClass,
					comparator: x.comparator,
					sort: x.sort,
					sortingOrder: x.sortingOrder,
					// suppressMenu: false,
					sortable: x.sortable,
					filter: x.filterable,
					resizable: x.resizable,
					width: x.width ? x.width : null,
					maxWidth: x.maxWidth ? x.maxWidth : null,
					minWidth: x.minWidth ? x.minWidth : null,
					cellRendererSelector: x.cellRendererSelector,
					valueGetter: valueGetter
				} as ColDef;
				if (x.renderer != "default") {
					switch (x.renderer) {
						case "details": {
							col["cellRenderer"] = DetailsComponent;
							break;
						}
						case "compliance": {
							col["cellRenderer"] = ComplianceComponent;
							break;
						}
						case "flagged": {
							col["cellRenderer"] = FlaggedComponent;
							break;
						}
						case "icon": {
							col["cellRenderer"] = IconComponent;
							break;
						}
						case "url": {
							col["cellRenderer"] = UrlComponent;
							break;
						}
					}
				}
                this.gridCols.push(col);
            }
        });


        this._addAdditionalColumns();

        this._changeDetectorRef.detectChanges();
    }


    @Input()
    set columns(cols: Column[]) {
        if (!cols)
            return;

        if (cols.length > 1)
            cols = cols.sort((a: Column, b: Column) => { return a.index < b.index ? -1 : 1 });
        let _domain$: Observable<LunaDomain>[] = [];
        //Now go through and check and columns for a domain formatter. We need to retrieve all the specified domains before we build the columns
        for (let x = 0; x < cols.length; x++) {
            if (cols[x].valueFormatter == ValueFormatter.Domain) {
                let typedOptions = cols[x].formatterOptions as FormatterOptions<DomainOptions>;

                let domainSub$ = this._domainService.get(typedOptions.params.domain).pipe(tap((data) => {
                    return data;
                }));

                _domain$.push(domainSub$);
            }
        }
        if (_domain$ == null || _domain$.length == 0) _domain$ = [of(null)];
        this.domains$ = forkJoin(_domain$).pipe(tap((domains) => {
            this.domains = domains;
            this.buildColumns(cols);
            if (this.grid && this.grid.api) this.grid.api.sizeColumnsToFit();
        }));       
    }

    get columns() {
        return this._columns;
    }

    get resolvedData() {
        return this._resolvedData;
    }

    @Input()
    set dataset(val: any) {
        let obs: Observable<any>;
        if (val instanceof Promise)
            obs = from(val);
        else if (!!val && (val instanceof Observable || (typeof val.lift === 'function' && typeof val.subscribe === 'function')))
            obs = val;
        else
            obs = of(val);

        this.data$ = this.domains$.pipe(switchMap(() => {
            return obs.pipe()
        }),
            tap((data: any[]) => {
                this._resolvedData = data;
                this.dataLoaded = true;
                this.onDataLoaded.emit(this.dataLoaded);
                if (this.columns && data && data.length > 0) {
                    this.columns.forEach(x => {
                        if (x.objectProperty && x.objectProperty.path && x.objectProperty.path.indexOf('$.') !== -1)
                            this._checkJSONPath(data, x.objectProperty.path);
                    });
                }
                // if (this.api != null) {
                //     this.api.setGridOption("loading", false);
                // }
                 this._changeDetectorRef.detectChanges();
            })
        );
       
    }

    get dataset() {
        return this.data$;
    }

    ngOnInit() {
        this.onDataLoaded.emit(this.dataLoaded);
    }

    ngAfterViewInit() {
        
        this.settings = this._settings;
        this.gridIsReady.subscribe({
            next: (evt: boolean) => {
                if (evt) {
                    this.subscribeToGridEvents();
                }
            }
        });    
        this.gridIsReady.emit(true);    
    }

    subscribeToGridEvents(): void {
        this.grid.gridReady.subscribe((result) => {
            this.grid.api.sizeColumnsToFit();

            if (!this._columns)
                return;

            //sort the first sortable column
            let col = this._columns.find(x => x.sortable);

			if (col && this.grid.api) {
                this.grid.api.applyColumnState({
                    sort: 'desc',
                    colId: col.name
                } as ApplyColumnStateParams);
            }
				
        });

        this.grid.selectionChanged.subscribe((result) => {
            let items = [];

			//let temp = result.api.selectionController.selectedNodes;
			let temp = this.grid.api.getSelectedNodes();
            for (let obj in temp) {
                if (temp[obj] && temp[obj].data)
                    items.push(temp[obj]);
            }

            let selectedItems = items;
            let data = [];
            selectedItems.forEach(selectedItem => {
                data.push(selectedItem.data);
            });

            this.selectItems = data;
        });
    }

    ngOnDestroy() {
        this._onSelectedItemsChange.complete();
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }

    constructor(
        public _injector: Injector,
        public _changeDetectorRef: ChangeDetectorRef,
        public _toolbarService: ToolBarService,
		public _domainService: DomainDefinitionService,
		public _formatter: GridFormatterService
    ) {
		// this.gridOptions = {};
    }

    public _addAdditionalColumns() {
        this._addSelectColumn();
        if (this._config?.displaySelectColumn && this.gridCols.length > 0) {
            const col = this.gridCols[0];
            const originalCellRendererSelector = col.cellRendererSelector;
            col.cellRendererSelector = (params) => {
                if (params.context?.selectionDisabled) {
                    return { component: SelectionIndicatorRendererComponent };
                }
                if (originalCellRendererSelector) {
                    return originalCellRendererSelector(params);
                }
                return undefined;
            };
        }
    }

    public _addSelectColumn() {
    }

    public _removeColumnById(id: string) {
        let index = this.gridCols.findIndex(x => x.colId === id);

        if (index > -1)
            this.gridCols.splice(index, 1);
    }

    public _itemClicked(val: { row: any, colHeader: string }) {
        let renderer = this.columns.find(x => x.name === val.colHeader).renderer;
        this.onRendererInteraction.emit({ rendererType: renderer, row: val.row, colHeader: val.colHeader });
    }

    public _checkJSONPath(objects: any[], property: string): void {
        let valid = false;

        for (let obj of objects) {
            try {
                if (jp.value(obj, property)) {
                    valid = true;
                    break;
                }
            } catch (error) { }
        }

        if (!valid)
            console.warn(`JSON path was not found in array object:: data-table (${property})`);
    }

    detectChanges() {
        this._changeDetectorRef.detectChanges();
    }

    disableSelection() {
        const opts = this.gridOptions;
        if (opts != null) {
            if (opts.rowSelection == null) {
                opts.rowSelection = { mode: 'multiRow', enableClickSelection: false, isRowSelectable: () => false } as RowSelectionOptions;
            } else {
                (opts.rowSelection as RowSelectionOptions).enableClickSelection = false;
                // Blocks keyboard (Space/Enter) toggling too - enableClickSelection only blocks the mouse.
                (opts.rowSelection as RowSelectionOptions).isRowSelectable = () => false;
            }
            if (opts.context == null) {
                opts.context = { selectionDisabled: true };
            } else {
                opts.context.selectionDisabled = true;
            }
        }
        if (this._config?.displaySelectColumn && this.api) {
            const rs = { ...(opts?.rowSelection as RowSelectionOptions), checkboxes: false, isRowSelectable: () => false };
            this.api.setGridOption('rowSelection', rs);
            this.api.redrawRows();
        }
    }

    enableSelection() {
        const opts = this.gridOptions;
        if (opts != null) {
            if (opts.rowSelection == null) {
                opts.rowSelection = { enableClickSelection: true } as RowSelectionOptions;
            } else {
                (opts.rowSelection as RowSelectionOptions).enableClickSelection = true;
                (opts.rowSelection as RowSelectionOptions).isRowSelectable = undefined;
            }
            if (opts.context == null) {
                opts.context = { selectionDisabled: false };
            } else {
                opts.context.selectionDisabled = false;
            }
        }
        if (this._config?.displaySelectColumn && this.api) {
            const rs = { ...(opts?.rowSelection as RowSelectionOptions), checkboxes: true, isRowSelectable: undefined };
            this.api.setGridOption('rowSelection', rs);
            this.api.redrawRows();
        }
    }


    deleteItems(items: any[]) {
        items.forEach(x => {
            let idx = this._resolvedData.findIndex(y => y === x);
            let idx2 = this._selectedItems.findIndex(y => y === x);

            if (idx != -1)
                this._resolvedData.splice(idx, 1);

            if (idx2 != -1)
                this._selectedItems.splice(idx2, 1);
        });

        this.grid.api.setGridOption('rowData', this.resolvedData);
    }

    clear() {
        this.grid.api.setGridOption('rowData', []);
        this._resolvedData = [];
        this._selectedItems = [];
    }
}

export const componentFrameworks = {
    details: DetailsComponent,
    compliance: ComplianceComponent,
    flagged: FlaggedComponent,
    icon: IconComponent,
    url: UrlComponent
};
